// src/services/permitsService.ts
// Fetches live permits-to-work from the Cloudflare Worker API (Neon database).
// Maps to the Permit shape Permits.tsx expects.
//
// Architecture: seed-from-API (same as timesheetsService).
// The Permits page uses useState(MOCK_PERMITS) and mutates state for
// approve/reject/start/complete actions. On first load, a useEffect merges
// Neon permits (deduplicating by ID) into that state so all existing
// mutation handlers continue to work unchanged.
//
// Complex nested fields (workers[], preStartItems[], closeOutItems[],
// atmospheric readings) default to empty/null — they are populated from
// full JSONB columns in the Neon schema via a future GET /api/whs/permits/:id
// call when the detail view is wired in Phase 6 step 3.

import { api } from '../utils/api'

interface ApiPermit {
  id:              string
  jobId?:          string
  job_id?:         string
  type?:           string
  status?:         string
  permitNumber?:   string
  permit_number?:  string
  requestedAt?:    string | null
  requested_at?:   string | null
  approvedAt?:     string | null
  approved_at?:    string | null
  validFrom?:      string | null
  valid_from?:     string | null
  validTo?:        string | null
  valid_to?:       string | null
  description?:    string | null
  location?:       string | null
  createdAt?:      string
  created_at?:     string
}

// Shape Permits.tsx expects — matches typeof MOCK_PERMITS[0]
export interface MappedPermit {
  id:                   string
  permitNumber:         string
  type:                 string
  status:               string
  jobId:                string
  jobNumber:            string    // enriched from jobs join — Phase 6
  zone:                 string
  location:             string
  description:          string
  plannedStart:         string
  plannedEnd:           string
  validFrom:            string | null
  validTo:              string | null
  requestedAt:          string | null
  approvedAt:           string | null
  approvedByName:       string | null
  submittedByName:      string
  // Complex nested fields — empty defaults until full detail endpoint
  workers:              never[]
  preStartItems:        never[]
  closeOutItems:        never[]
  linkedSwmsId?:        string
  linkedSwmsNumber?:    string
  bydaReference?:       string
  energexReference?:    string
  preStartSignedAt?:    string
  closeOutSignedAt?:    string
  closeOutSignedByName?: string
}

function mapApiPermit(raw: ApiPermit): MappedPermit {
  const plannedStart = raw.validFrom ?? raw.valid_from ?? raw.requestedAt ?? raw.requested_at ?? new Date().toISOString()
  const plannedEnd   = raw.validTo   ?? raw.valid_to   ?? plannedStart

  return {
    id:              raw.id,
    permitNumber:    raw.permitNumber    ?? raw.permit_number    ?? `PTW-${raw.id.slice(-6).toUpperCase()}`,
    type:            raw.type           ?? 'EXCAVATION',
    status:          raw.status         ?? 'DRAFT',
    jobId:           raw.jobId          ?? raw.job_id           ?? '',
    jobNumber:       '',                   // enriched from jobs join — Phase 6
    zone:            raw.location       ?? 'Site works',
    location:        raw.location       ?? '',
    description:     raw.description    ?? '',
    plannedStart,
    plannedEnd,
    validFrom:       raw.validFrom      ?? raw.valid_from       ?? null,
    validTo:         raw.validTo        ?? raw.valid_to         ?? null,
    requestedAt:     raw.requestedAt    ?? raw.requested_at     ?? null,
    approvedAt:      raw.approvedAt     ?? raw.approved_at      ?? null,
    approvedByName:  null,
    submittedByName: '',
    workers:         [],
    preStartItems:   [],
    closeOutItems:   [],
  }
}

export async function fetchPermits(jobId?: string): Promise<MappedPermit[]> {
  const params = jobId ? `?jobId=${jobId}` : ''
  const { data } = await api.get<{ data: ApiPermit[] } | ApiPermit[]>(`/api/whs/permits${params}`)
  const raw: ApiPermit[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiPermit)
}
